import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { useTooltip } from 'components/Tooltip';
import { MAX_SHARE_NOTE_NAME_LENGTH } from 'utils/constants';
import './styles.scss';

const Note = ({
  id,
  title,
  imageUrls,
  shared,
  addToShare,
  removeShare,
}) => {
  const ref = useRef();
  const imageUrl = imageUrls?.rect260x175 ? imageUrls.rect260x175.url : null;
  const isShared = shared?.findIndex(item => item?.id === id) > -1;
  const {
    setCurrentTitle,
    setAnchor,
  } = useTooltip();

  const updateTooltip = () => {
    setCurrentTitle(title);
    setAnchor(ref.current);
  };

  const resetTooltip = () => {
    setCurrentTitle(null);
    setAnchor(null);
  };

  return (
    <div
      className={clsx('note-item', !imageUrl && 'no-image', isShared && 'selected')}
      onClick={() => {
        if (isShared) {
          removeShare(id);
        } else {
          addToShare(id);
        }
      }}
    >
      <div className="note-item__avatar">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            onDragStart={(e) => {
            e.preventDefault();
          }}
          />
      ) : null}
      </div>
      <div
        className="note-item__name"
        onMouseEnter={updateTooltip}
        onMouseMove={updateTooltip}
        onMouseLeave={resetTooltip}
      >
        <div
          ref={ref}
          className="note-item__name-text"
        >
          {title.length <= MAX_SHARE_NOTE_NAME_LENGTH
            ? title
            : `${title.slice(0, MAX_SHARE_NOTE_NAME_LENGTH)}...`}
        </div>
      </div>
    </div>
  );
};

const NoteGroup = ({
  groupIndex,
  name,
  expand = true,
  notes,
  isGroup,
  viewType,
  isLast = false,
  ...restProps
}) => {

  const emptyNoteGroupStyle = notes.length === 0 ? 'note-group__empty' : '';

  return isGroup ? (
    <div className={clsx(
      'note-group grid',
      { 'open': notes?.length > 0 },
      groupIndex === 0 ? 'first' : null,
      isLast ? 'last' : null,
      )}
    >
      <div className="note-group__head">
        <div className="note-group__center">
          <span className="note-group__title">
            {name}
          </span>
        </div>
      </div>

      <div className={clsx('note-group__body', viewType, { 'hide': !expand })}>
        {notes?.map((item, index) => (
          <Note
            key={`note_${index}`}
            {...restProps}
            {...item}
          />
        ))}
      </div>
    </div>
  ) : (
    <div
      className={clsx('notes-list', viewType, emptyNoteGroupStyle)}
    >
      {notes?.map((item, index) => (
        <Note
          key={`note_${index}`}
          {...restProps}
          {...item}
        />
      ))}
    </div>
  );
};

const NoteListForm = ({
  items,
  groups,
  onNext,
  onCopyLink,
  isLast,
  ...restProps
}) => {

  const containerClass = 'notes-list--grid';
  const sortFn = (asc = false) => {
    return (a, b) => {
      if (a.order > b.order) return asc ? 1 : -1;
      if (a.order < b.order) return asc ? -1 : 1;
      return 0;
    };
  };

  return (
    <>
      <p className="studio-share-modal__intro share-note-modal__intro intro-text">
        Select the items you’d like to share below, and we’ll bundle them up for you to share via link or email.
        Recipients will always see the most current version of each item shared.
        Learn more about your sharing options{' '}
        <a
          href="https://help.aisleplanner.com/en/articles/2083600-sharing-style-guides-color-palettes
          -notes-with-vendors-outside-of-aisle-planner"
          target="_blank"
          rel="noreferrer"
        >
          here
        </a>
        .
      </p>
      {items.length === 0 ? (
        <div className="empty-share-step">
          <p className="empty-share-step__text">
            It looks like you don’t have any notes to share just yet. Click below to select other items to share.
          </p>
        </div>
      ) : (
        <div>
          <NoteGroup
            viewType={containerClass}
            notes={items
              .filter((item) => item.groupId === null)
              .sort(sortFn())}
            key="nogroup"
            id="nogroup"
            {...restProps}
          />
          <div className="group-list">
            {groups.map((group, index) => (
              <NoteGroup
                key={group.id}
                id={group.id}
                groupIndex={index}
                name={group.name}
                order={group.order}
                expand={group.expand}
                viewType={containerClass}
                notes={items
                  .filter((item) => item.groupId === group.id)
                  .sort(sortFn())}
                isGroup
                isLast={index === groups.length - 1}
                {...restProps}
              />
            ))}
          </div>
        </div>
      )}
      <div className="share-form__buttons">
        {isLast && (
          <button
            type="button"
            className="button"
            onClick={onCopyLink}
          >
            Create Link
          </button>
        )}
        <button
          type="button"
          className="button"
          onClick={onNext}
        >
          Next
        </button>
      </div>
    </>
  );
};

NoteListForm.propTypes = {
  items: PropTypes.array,
  groups: PropTypes.array,
  onNext: PropTypes.func,
  shared: PropTypes.array,
  addToShare: PropTypes.func,
  removeShare: PropTypes.func,
};

NoteListForm.defaultProps = {};

export default NoteListForm;
